import { z } from 'zod';
import type { EndReason, UserId } from '../../types';

export const CHESS_ID = 'chess';

export type ChessColor = 'w' | 'b';

export const COLOR_LABEL: Record<ChessColor, string> = {
  w: 'Blancs',
  b: 'Noirs',
};

/** Type de pièce, notation algébrique anglaise utilisée par chess.js. */
export type PieceType = 'p' | 'n' | 'b' | 'r' | 'q' | 'k';

/** Un pion ne peut promouvoir qu'en dame, tour, fou ou cavalier. */
export type PromotionPiece = 'q' | 'r' | 'b' | 'n';

export const PIECE_LABEL: Record<PieceType, string> = {
  p: 'Pion',
  n: 'Cavalier',
  b: 'Fou',
  r: 'Tour',
  q: 'Dame',
  k: 'Roi',
};

/** Détail de la fin de partie, plus précis que `EndReason`. */
export type ChessOutcome =
  | 'checkmate'
  | 'stalemate'
  | 'insufficient_material'
  | 'threefold_repetition'
  | 'fifty_moves'
  | 'resign'
  | 'timeout'
  | 'admin';

export const OUTCOME_LABEL: Record<ChessOutcome, string> = {
  checkmate: 'Échec et mat',
  stalemate: 'Pat',
  insufficient_material: 'Matériel insuffisant',
  threefold_repetition: 'Triple répétition',
  fifty_moves: 'Règle des 50 coups',
  resign: 'Abandon',
  timeout: 'Temps écoulé',
  admin: 'Interrompue par un administrateur',
};

export interface ChessMoveRecord {
  san: string;
  from: string;
  to: string;
  color: ChessColor;
  piece: PieceType;
  captured?: PieceType;
  promotion?: PromotionPiece;
  check: boolean;
  checkmate: boolean;
  /** Position après le coup, en notation FEN. */
  fen: string;
}

export interface ChessConfig {
  /** Secondes accordées par coup ; `null` désactive le chronomètre. */
  turnSeconds: number | null;
}

export const DEFAULT_CHESS_CONFIG: ChessConfig = { turnSeconds: 120 };

export type ChessPhase = 'playing' | 'finished';

/**
 * État d'une partie d'échecs. Les échecs sont un jeu à information complète :
 * l'état est identique pour les deux joueurs, `viewFor()` ne masque rien.
 */
export interface ChessState {
  version: 1;
  phase: ChessPhase;
  /** Sièges : `seats[0]` joue les blancs, `seats[1]` les noirs. */
  seats: UserId[];
  fen: string;
  history: ChessMoveRecord[];
  turn: UserId | null;
  winner: UserId | null;
  endReason: EndReason | null;
  outcome: ChessOutcome | null;
  config: ChessConfig;
  deadlineAt: number | null;
  moves: number;
}

export interface LegalMove {
  from: string;
  to: string;
  san: string;
  promotion?: PromotionPiece;
}

/** Projection destinée à un joueur (ou à un observateur en fin de partie). */
export interface ChessView {
  phase: ChessPhase;
  youId: UserId;
  opponentId: UserId;
  /** Couleur du destinataire ; sert à orienter l'échiquier. */
  color: ChessColor;
  fen: string;
  turn: UserId | null;
  turnColor: ChessColor;
  winner: UserId | null;
  endReason: EndReason | null;
  outcome: ChessOutcome | null;
  check: boolean;
  /** Coups légaux du destinataire — vide si ce n'est pas son tour. */
  legalMoves: LegalMove[];
  history: ChessMoveRecord[];
  lastMove: { from: string; to: string } | null;
  /** Pièces capturées, par couleur du camp qui les a perdues. */
  captured: Record<ChessColor, PieceType[]>;
  config: ChessConfig;
  deadlineAt: number | null;
  moves: number;
}

const square = z
  .string()
  .regex(/^[a-h][1-8]$/, 'case invalide (exemple : e4)');

export const chessActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('move'),
    from: square,
    to: square,
    promotion: z.enum(['q', 'r', 'b', 'n']).optional(),
  }),
  z.object({ type: z.literal('resign') }),
]);

export type ChessAction = z.infer<typeof chessActionSchema>;

/** Niveaux de difficulté proposés pour les parties contre l'ordinateur. */
export const CHESS_LEVELS = [
  { id: 'facile', label: 'Facile' },
  { id: 'moyen', label: 'Moyen' },
  { id: 'difficile', label: 'Difficile' },
] as const;

export type ChessLevel = (typeof CHESS_LEVELS)[number]['id'];
